import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import paperFirstSectionTopDesktop from '../../assets/paper/top-firstSection-desktop.svg';
import paperFirstSectionTopMobile from '../../assets/paper/top-firstSection-mobile.svg';
import paperSubtleTopDesktop from '../../assets/paper/top-subtle-desktop.svg';
import paperSubtleTopMobile from '../../assets/paper/top-subtle-mobile.svg';
import paperFirstSectionBottomDesktop from '../../assets/paper/bottom-firstSection-desktop.svg';
import paperFirstSectionBottomMobile from '../../assets/paper/bottom-firstSection-mobile.svg';
import paperSubtleBottomDesktop from '../../assets/paper/bottom-subtle-desktop.svg';
import paperSubtleBottomMobile from '../../assets/paper/bottom-subtle-mobile.svg';

import { colors, media } from '../styles/index';

const propTypes = {
  children: PropTypes.any,
  top: PropTypes.bool,
  bottom: PropTypes.bool,
  subtle: PropTypes.bool,
  overlay: PropTypes.bool,
  overlayMargin: PropTypes.number,
  mobileOverlayMargin: PropTypes.number
};

/**
 * The Paper component, used to provide a beige background for pages/components.
 *
 * @param {bool} top - if the paper tear texture should be at the top of the section
 * @param {bool} bottom - if the paper tear texture should be at the bottom of the section
 * @param {bool} subtle - indicates if the subtle paper tear texture should be used
 * @param {bool} overlay - if there is a component that will overlay on top of the paper tear texture
 * @param {Number} overlayMargin - the margin such that the paper tear is centered behind the
 * component that is overlaying the paper tear texture (default 200)
 * @param {Number} mobileOverlayMargin - if desired, allows an overlay margin to be specified for mobile
 *
 * @returns {JSX}
 */
const Paper = props => {
  const {
    children,
    top = false,
    bottom = false,
    subtle = false,
    overlay = false,
    overlayMargin = 200,
    mobileOverlayMargin = 0
  } = props;

  const renderContentComponent = () => {
    return (
      <Content
        top={top}
        bottom={bottom}
        overlay={overlay}
        overlayMargin={overlayMargin}
        mobileOverlayMargin={mobileOverlayMargin}
      >
        {children}
      </Content>
    );
  };

  return (
    <PaperWrapper>
      {top && (
        <PaperTop
          subtle={subtle}
          overlay={overlay}
          overlayMargin={overlayMargin}
          mobileOverlayMargin={mobileOverlayMargin}
        />
      )}
      {renderContentComponent()}
      {bottom && (
        <PaperBottom
          subtle={subtle}
          overlay={overlay}
          overlayMargin={overlayMargin}
          mobileOverlayMargin={mobileOverlayMargin}
        />
      )}
    </PaperWrapper>
  );
};

Paper.propTypes = propTypes;

/**
 *!  NOTE: For some reason, setting the height of the top/bottom divs equal to the height of the background
 *! causes tiny borders of the paper color to appear. To get around this, the divs have been positioned
 *! relative and adjusted slightly to cover the gap.
 */

const PaperWrapper = styled.div`
  position: relative;
  background-color: ${colors.sandBeige};
`;

const PaperTop = styled.div`
  margin-top: ${({ overlay, overlayMargin }) => (overlay ? `-${overlayMargin}px` : '')};
  height: ${({ subtle }) => (subtle ? '94px' : '100px')};
  width: 100%;
  background-image: ${({ subtle }) =>
    subtle ? `url('${paperSubtleTopDesktop}')` : `url('${paperFirstSectionTopDesktop}')`};
  background-repeat: repeat-x;
  background-size: auto ${({ subtle }) => (subtle ? '94px' : '100px')};
  position: relative;
  top: -1px;

  @media screen and (max-width: ${media.mediumTop}) {
    margin-top: ${({ overlay, mobileOverlayMargin }) => (overlay ? `-${mobileOverlayMargin}px` : '')};
  }

  @media (max-width: ${media.smallTop}) {
    height: 37px;
    background-image: ${({ subtle }) =>
      subtle ? `url('${paperSubtleTopMobile}')` : `url('${paperFirstSectionTopMobile}')`};
    background-size: auto 37px;
  }
`;

const Content = styled.div`
  padding-top: ${({ top, overlay, overlayMargin }) =>
    top && overlay ? `calc(.5 * ${overlayMargin}px + 120px)` : top ? '120px' : ''};
  padding-bottom: ${({ bottom, overlay, overlayMargin }) => (bottom && overlay ? `calc(.5 * ${overlayMargin}px)` : '')};

  @media screen and (max-width: ${media.mediumTop}) {
    padding-top: ${({ top, overlay, mobileOverlayMargin }) =>
      top && overlay ? `calc(.5 * ${mobileOverlayMargin}px + 60px)` : top ? '60px' : ''};
    padding-bottom: ${({ bottom, overlay, mobileOverlayMargin }) =>
      bottom && overlay ? `calc(.5 * ${mobileOverlayMargin}px)` : ''};
  }
`;

const PaperBottom = styled.div`
  margin-bottom: ${({ overlay, overlayMargin }) => (overlay ? `-${overlayMargin}px` : '')};
  height: ${({ subtle }) => (subtle ? '94px' : '100px')};
  width: 100%;
  background-image: ${({ subtle }) =>
    subtle ? `url('${paperSubtleBottomDesktop}')` : `url('${paperFirstSectionBottomDesktop}')`};
  background-repeat: repeat-x;
  background-size: auto ${({ subtle }) => (subtle ? '94px' : '100px')};
  position: relative;
  top: 2px;

  @media screen and (max-width: ${media.mediumTop}) {
    margin-bottom: ${({ overlay, mobileOverlayMargin }) => (overlay ? `-${mobileOverlayMargin}px` : '')};
  }

  @media (max-width: ${media.smallTop}) {
    height: 38px;
    background-image: ${({ subtle }) =>
      subtle ? `url('${paperSubtleBottomMobile}')` : `url('${paperFirstSectionBottomMobile}')`};
    background-size: auto 38px;
    top: 3px;
  }
`;

export { Paper };
